import React, { useCallback } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import {
  ArrowForward,
  HashOutline,
  NucleoDialogue1On1ChatTalk,
  RefreshOutline,
  StarOutline,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, LinkButton, Stack, Typography } from '@lyearn/ui';
import Loader from '@/components/Loader';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { PARENT_ENTITY_ID } from '@/modules/corporate/modules/exercise/views/Details/utils';
import { useGetRoleplayQuery } from '@/modules/corporate/roleplay/graphql/queries/generated/getRoleplay';
import { RoleplayDetailsPerformanceFragment } from '@/modules/corporate/roleplay/views/RoleplayDetailsView/graphql/fragments/generated/RoleplayDetailsPerformance';
import { useRoleplayDetailsPerformanceQuery } from '@/modules/corporate/roleplay/views/RoleplayDetailsView/graphql/queries/generated/roleplayDetailsPerformance';
import { getAllowedAttempts } from '@/modules/corporate/roleplay/views/RoleplayDetailsView/utils/getCanRetakeRoleplay';
import roleplayRoutes from '@/pages/corporate/routes/contents/roleplay';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { RoleplayContentType } from '@/types/schema';

import { roleplayCardMobileStyles, roleplayCardStyles } from './styles';

interface RoleplayEmbedCardProps {
  roleplayId: string;
  courseId?: string;
}

const PILL_CLASS =
  'rounded-[6px] bg-white pl-[8px] pr-[10px] py-[5px] shadow-[0px_0px_0px_1px_#EAEAEA]';
const ICON_CLASS = 'h-[16px] w-[16px] shrink-0 text-[#888]';
const PILL_TEXT_CLASS = 'whitespace-nowrap text-text-secondary whitespace-pre';

const RoleplayEmbedCard: React.FC<RoleplayEmbedCardProps> = ({ roleplayId, courseId }) => {
  const { t } = useTranslation(['Common', 'Course', 'Roleplay']);
  const { isMobile } = useScreen();
  const isAdminRoute = useAdminRoute();
  const navigation = useNavigation();

  const user = useUserContext();
  const [{ data, fetching }] = useGetRoleplayQuery({ variables: { roleplayId } });
  const [{ data: performanceData }] = useRoleplayDetailsPerformanceQuery({
    variables: { contentId: roleplayId, userId: user.id, parentEntityId: courseId! },
    pause: !courseId,
    requestPolicy: 'cache-first',
  });

  const handleView = useCallback(() => {
    if (isAdminRoute) {
      navigation.push(
        courseId
          ? roleplayRoutes.CourseRoleplayDetails(roleplayId, courseId)
          : roleplayRoutes.AdminRoleplayDetails(roleplayId),
      );
      return;
    }
    const base = roleplayRoutes.RoleplayDetails(roleplayId);
    const route = courseId ? `${base}?${PARENT_ENTITY_ID}=${courseId}` : base;
    navigation.push(route);
  }, [courseId, isAdminRoute, navigation, roleplayId]);

  if (fetching) {
    return (
      <Box className="my-24 flex items-center justify-center py-32">
        <Loader />
      </Box>
    );
  }

  const roleplay =
    data?.content?.__typename === 'RoleplayContentType'
      ? (data.content as RoleplayContentType)
      : null;

  if (!roleplay) return null;

  const criteriaCount = roleplay.criteria?.length ?? 0;
  const allowedAttempts = getAllowedAttempts(roleplay);
  const fetchedPerformance = performanceData?.roleplayPerformances?.edges?.[0]?.node as
    | RoleplayDetailsPerformanceFragment
    | undefined;
  const attemptsUsed = fetchedPerformance?.attempts?.length ?? 0;

  // Show current/max when in course view context, otherwise just max (builder preview)
  const attemptsText =
    courseId !== undefined
      ? t('Roleplay:AttemptsInfo', { count: attemptsUsed, max: allowedAttempts })
      : t('Course:Builder.RoleplayAttempts', { count: allowedAttempts });

  return (
    <Box
      className={cn(
        'relative my-16 rounded-[16px] border border-[#EAEAEA] bg-bg-primary p-[20px] shadow-[0px_10px_10px_rgba(0,0,0,0.04),0px_2px_3px_rgba(0,0,0,0.04),0px_0px_0.5px_rgba(0,0,0,0.04)]',
        isMobile ? roleplayCardMobileStyles : roleplayCardStyles,
      )}>
      <Stack alignItems="start" direction="row" gapX={16}>
        <Stack
          alignItems="center"
          className="h-[44px] w-[44px] shrink-0 bg-[#7C3AED] rounded-full"
          justifyContent="center">
          <NucleoDialogue1On1ChatTalk className="text-text-white" height={24} width={24} />
        </Stack>
        <Stack direction="col" gap={0}>
          <Typography color="text-text-secondary" variant="label-caps">
            {t('Common:ResourceType.Roleplay')}
          </Typography>
          <Typography className="text-text-primary" variant="heading-xs">
            {roleplay.name}
          </Typography>
        </Stack>
      </Stack>

      <Stack alignItems="center" className="mt-16" direction="row" justifyContent="between">
        <Stack className="flex-wrap" gap={12}>
          {criteriaCount > 0 ? (
            <Stack alignItems="center" className={PILL_CLASS} gapX={6}>
              <HashOutline className={ICON_CLASS} />
              <Typography className={PILL_TEXT_CLASS} variant="body-short-02">
                {t('Course:Builder.RoleplayCriteria', { count: criteriaCount })}
              </Typography>
            </Stack>
          ) : null}

          {roleplay.passingScore ? (
            <Stack alignItems="center" className={PILL_CLASS} gapX={6}>
              <StarOutline className={ICON_CLASS} />
              <Typography className={PILL_TEXT_CLASS} variant="body-short-02">
                {t('Course:Builder.RoleplayPassingScore', { score: roleplay.passingScore })}
              </Typography>
            </Stack>
          ) : null}

          <Stack alignItems="center" className={PILL_CLASS} gapX={6}>
            <RefreshOutline className={ICON_CLASS} />
            <Typography className={PILL_TEXT_CLASS} variant="body-short-02">
              {attemptsText}
            </Typography>
          </Stack>
        </Stack>
        <LinkButton className="flex items-center justify-center gap-8" onClick={handleView}>
          <Typography color="text-text-primary" variant="heading-xxs">
            {t('Common:Actions.View')}
          </Typography>
          <ArrowForward className="h-[20px] w-[20px] text-[#222]" />
        </LinkButton>
      </Stack>
    </Box>
  );
};

export default RoleplayEmbedCard;
